import { API_ROUTES, ApiService } from '@shared/api-utils';
import { ROUTES } from '@shared/utils';
import type { OpenDepositSheetOptions } from '../../global-sheets/context/GlobalSheetContext';
import type { ScratchcardDetails } from '../../scratchcards/types';
import { resolveScratchcardMappingPath } from '../../scratchcards/utils/resolveScratchcardMappingPath';

type RefetchFunction = () => Promise<void>;

type HandlerOfferData = {
  bundleCode?: string;
  offerCode?: string;
  productIds?: string[] | string;
  productURL?: string;
  minimumAmountForBonus?: number;
  redirectUrlOnInsufficientDeposit?: string;
};

type OpenDepositSheetFunction = (options?: OpenDepositSheetOptions) => void;

type OfferActionHandler = (offerData?: HandlerOfferData) => void | Promise<void>;

type OfferActionDeps = {
  navigate: (url: string) => void;
  refetch?: RefetchFunction;
  openDepositSheet?: OpenDepositSheetFunction;
  scratchcards?: ScratchcardDetails[];
};

const { OFFER_BUNDLES } = API_ROUTES;
const { CASINO, LOTTO, SCRATCHCARDS, SPORTS, SYNDICATES, HOME } = ROUTES;

function createOfferBundleActionHandlers({ navigate, refetch, openDepositSheet, scratchcards }: OfferActionDeps) {
  const handleOptInAction: OfferActionHandler = async offerData => {
    const apiService = ApiService.getInstance();
    await apiService.post(OFFER_BUNDLES.OPT_IN, {
      offerBundleCode: offerData?.bundleCode,
    });
    refetch?.();
  };

  const handleClaimAction: OfferActionHandler = async offerData => {
    const apiService = ApiService.getInstance();
    await apiService.post(OFFER_BUNDLES.CLAIM_OFFER, {
      offerBundleCode: offerData?.bundleCode,
      offerCode: offerData?.offerCode,
    });
    refetch?.();
  };

  const handleAllVerticalsAction: OfferActionHandler = () => {
    navigate(HOME);
  };

  const handleAllGamesAction: OfferActionHandler = () => {
    navigate(CASINO.DISCOVERY);
  };

  const handleSingleGameAction: OfferActionHandler = offerData => {
    if (offerData?.productIds) {
      navigate(CASINO.GAME(offerData.productIds as string));
    }
  };

  const handleAllLotteriesAction: OfferActionHandler = () => {
    navigate(LOTTO.DISCOVERY);
  };

  const handleSingleLotteryAction: OfferActionHandler = offerData => {
    if (offerData?.productIds) {
      const lotteryId = (offerData.productIds as string).toUpperCase() as keyof typeof LOTTO.LOTTERIES;
      navigate(LOTTO.LOTTERIES[lotteryId]);
    }
  };

  const handleAllScratchcardsAction: OfferActionHandler = () => {
    navigate(SCRATCHCARDS.DISCOVERY);
  };

  const handleSingleScratchcardAction: OfferActionHandler = offerData => {
    if (!scratchcards?.length) {
      navigate(SCRATCHCARDS.DISCOVERY);
      return;
    }

    const mappingPath = resolveScratchcardMappingPath(scratchcards, offerData?.productIds as string);
    navigate(mappingPath ? SCRATCHCARDS.GAME(mappingPath) : SCRATCHCARDS.DISCOVERY);
  };

  const handleSportsbookAction: OfferActionHandler = () => {
    navigate(SPORTS.DISCOVERY);
  };

  const handleSyndicatesAction: OfferActionHandler = () => {
    navigate(SYNDICATES);
  };

  const withProductUrl =
    (fallbackHandler: OfferActionHandler): OfferActionHandler =>
    offerData => {
      if (offerData?.productURL) {
        navigate(offerData.productURL);
        return;
      }

      return fallbackHandler(offerData);
    };

  const handleDepositAction: OfferActionHandler = offerData => {
    // Convert minimumAmountForBonus from pence to pounds for initial amount field
    const initialAmount = offerData?.minimumAmountForBonus ? String(offerData.minimumAmountForBonus / 100) : undefined;

    openDepositSheet?.({
      onSuccessCallback: () => {
        refetch?.();
      },
      minimumAmountForBonus: offerData?.minimumAmountForBonus,
      initialAmount,
      ...(offerData?.redirectUrlOnInsufficientDeposit && {
        redirectUrlOnInsufficientDeposit: offerData.redirectUrlOnInsufficientDeposit,
      }),
    });
  };

  // Action mapping object
  return {
    OPT_IN: handleOptInAction,

    DEPOSIT: handleDepositAction,
    //SOLVE_DEPOSIT_FIRST_CONFLICT -> not implemented yet

    CLAIM_BONUS_FIXED_AMOUNT: handleClaimAction,
    CLAIM_BONUS_PERCENTAGE_AMOUNT: handleClaimAction,
    CLAIM_VOUCHER_FIXED_AMOUNT: handleClaimAction,
    CLAIM_FREE_SPINS: handleClaimAction,
    CLAIM_SPORTSBOOK_FREE_BET: handleClaimAction,
    CLAIM_SPORTSBOOK_FREE_BET_WITH_AMOUNT: handleClaimAction,
    CLAIM_SPORTSBOOK_FREE_BET_CONDITIONAL: handleClaimAction,
    CLAIM_SPORTSBOOK_BETSLIP_BOOST: handleClaimAction,

    STAKE_ALL_GAMES: withProductUrl(handleAllGamesAction),
    STAKE_GAMES_TEMPLATE: handleSingleGameAction,
    STAKE_SINGLE_GAME: withProductUrl(handleSingleGameAction),
    STAKE_ALL_LOTTERIES: withProductUrl(handleAllLotteriesAction),
    STAKE_SINGLE_LOTTERY: withProductUrl(handleSingleLotteryAction),
    STAKE_ALL_SCRATCHCARDS: withProductUrl(handleAllScratchcardsAction),
    STAKE_SINGLE_SCRATCHCARD: withProductUrl(handleSingleScratchcardAction),

    PLAY_ALL_VERTICALS: withProductUrl(handleAllVerticalsAction),
    PLAY_SYNDICATES: withProductUrl(handleSyndicatesAction),
    PLAY_ALL_GAMES: withProductUrl(handleAllGamesAction),
    PLAY_SINGLE_GAME: withProductUrl(handleSingleGameAction),
    PLAY_GAMES_TEMPLATE: handleSingleGameAction,
    PLAY_ALL_LOTTERIES: withProductUrl(handleAllLotteriesAction),
    PLAY_SINGLE_LOTTERY: withProductUrl(handleSingleLotteryAction),
    PLAY_ALL_SCRATCHCARDS: withProductUrl(handleAllScratchcardsAction),
    PLAY_SINGLE_SCRATCHCARD: withProductUrl(handleSingleScratchcardAction),
    PLAY_SPORTSBOOK: withProductUrl(handleSportsbookAction),
    PLAY_SPORTSBOOK_FREE_BET: withProductUrl(handleSportsbookAction),
    PLAY_SPORTSBOOK_FREE_BET_CONDITIONAL: withProductUrl(handleSportsbookAction),
    PLAY_SPORTSBOOK_BETSLIP_BOOST: withProductUrl(handleSportsbookAction),
    PLAY_FREE_SPINS: withProductUrl(handleSingleGameAction),
  } satisfies Record<string, OfferActionHandler>;
}

type OfferBundleActionHandlersMap = ReturnType<typeof createOfferBundleActionHandlers>;

export { createOfferBundleActionHandlers, type HandlerOfferData, type OfferActionDeps, type OfferBundleActionHandlersMap, type OpenDepositSheetFunction, type RefetchFunction };
